import { Check, LoaderCircle } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

/**
 * Un champ qui s'enregistre SEUL, à la sortie du champ.
 *
 * ⚠️ Ce n'est PAS le modèle du proto, contrairement à ce que disait ce commentaire.
 * Son `InputEditable` affiche « Annuler » et « Sauvegarder » : la sauvegarde y est
 * un GESTE. Le choix inverse se défend ici, et il se défend pour une raison qui
 * n'existe pas là-bas : une entreprise sans compte qui remplit huit champs et
 * ferme l'onglet ne revient pas, et il n'y a aucun brouillon à retrouver puisqu'il
 * n'y a pas de compte. Mais c'est un écart assumé, pas une reprise.
 *
 * La MISE EN PAGE, elle, suit le proto (`molecules/Input.tsx`) :
 *   - le libellé est en 14/20 REGULIER, couleur `mg-secondary` — pas en 13/18
 *     semi-gras couleur d'encre, ce qui durcissait tout l'écran ;
 *   - le compteur descend SOUS le champ, sur la ligne du message d'erreur.
 *
 * L'état d'enregistrement est VISIBLE et éphémère. Un champ qui se sauve en silence
 * laisse le doute ; un bandeau permanent « tout est enregistré » ne se lit plus.
 */

type State = "idle" | "saving" | "saved" | "failed";

/**
 * LE libellé d'un champ, le même partout : dépôt d'offre, réglages de l'école,
 * fiche entreprise, portes à code.
 *
 * Il était rendu de trois façons (13/600 couleur d'encre avec une étoile rouge sur
 * le dépôt, 14/400 secondaire sans aucune marque ailleurs, 14/500 sur les portes),
 * si bien que « Titre de l'envoi » était obligatoire sans le dire. C'est celui du
 * proto (`molecules/Input.tsx`) qui reste, et l'étoile le suit partout.
 *
 * L'étoile est `aria-hidden` : c'est `aria-required` sur le contrôle qui l'annonce,
 * et un lecteur d'écran qui lirait aussi « étoile » le dirait deux fois.
 */
export function FieldLabel({
	label,
	htmlFor,
	required = false,
	className = "",
}: {
	label: string;
	htmlFor?: string;
	required?: boolean;
	className?: string;
}) {
	const contenu = (
		<>
			{label}
			{required ? (
				<span aria-hidden="true" className="text-mg-error-fg">
					{" *"}
				</span>
			) : null}
		</>
	);
	const classes = `t-body mb-1 block text-mg-secondary ${className}`;
	return htmlFor ? (
		<label htmlFor={htmlFor} className={classes}>
			{contenu}
		</label>
	) : (
		<span className={classes}>{contenu}</span>
	);
}

/**
 * L'`aria-describedby` d'un contrôle : la ligne sous le champ (aide OU erreur, elles
 * occupent la même place) et le compteur. Sans lui, l'aide et le plafond ne sont
 * lus qu'à l'œil.
 */
export function fieldDescription(
	id: string,
	{ message, counter = false }: { message: boolean; counter?: boolean },
): string | undefined {
	return (
		[message ? `${id}-aide` : null, counter ? `${id}-compteur` : null].filter(Boolean).join(" ") ||
		undefined
	);
}

export function EditableField({
	label,
	hint,
	error,
	counter,
	children,
	state = "idle",
	htmlFor,
	required = false,
}: {
	label: string;
	hint?: string;
	/** Ce qui ne va pas dans la valeur saisie ; prend la place de l'aide. */
	error?: string | null;
	/** « 42 / 70 » quand le champ est plafonné : la limite se voit avant d'être atteinte. */
	counter?: string;
	children: ReactNode;
	/** Absent pour un champ de GESTE, qui part avec un bouton et ne s'enregistre pas seul. */
	state?: State;
	/**
	 * L'`id` du contrôle : le libellé devient alors un vrai `label`, et l'aide et le
	 * compteur prennent les identifiants que rend `fieldDescription`.
	 */
	htmlFor?: string;
	/** Affiche l'étoile ; le contrôle, lui, porte `aria-required`. */
	required?: boolean;
}) {
	return (
		<div>
			<FieldLabel label={label} htmlFor={htmlFor} required={required} />
			{children}
			<div className="mt-1 flex items-start justify-between gap-3">
				{error ? (
					<p
						id={htmlFor ? `${htmlFor}-aide` : undefined}
						role="alert"
						className="t-caption-strong text-mg-error-fg"
					>
						{error}
					</p>
				) : (
					<p
						id={htmlFor && hint ? `${htmlFor}-aide` : undefined}
						className="t-caption text-mg-secondary"
					>
						{hint}
					</p>
				)}
				<span className="t-caption flex shrink-0 items-center gap-1.5 text-mg-secondary">
					{counter ? <span id={htmlFor ? `${htmlFor}-compteur` : undefined}>{counter}</span> : null}
					{state === "saving" ? <LoaderCircle className="size-3.5 animate-spin" /> : null}
					{state === "saved" ? (
						<span className="flex items-center gap-1 text-mg-success-fg">
							<Check className="size-3.5" />
							Enregistré
						</span>
					) : null}
					{state === "failed" ? <span className="text-mg-error-fg">Non enregistré</span> : null}
				</span>
			</div>
		</div>
	);
}

/**
 * La mécanique d'enregistrement, séparée du rendu.
 *
 * Elle n'envoie QUE si la valeur a changé : sortir d'un champ sans y toucher est le
 * geste le plus courant d'un formulaire, et il ne doit pas écrire.
 */
export function useFieldSave<T>(initial: T, save: (value: T) => Promise<boolean>) {
	const [value, setValue] = useState<T>(initial);
	const [state, setState] = useState<State>("idle");
	const saved = useRef(initial);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => () => clearTimeout(timer.current ?? undefined), []);

	/**
	 * `next` sert aux champs qui s'enregistrent AU CHOIX et non à la sortie : une
	 * case à cocher, un menu déroulant. Appelé depuis le `onChange`, `value` porte
	 * encore l'ancienne valeur — le `setValue` du même tour n'est pas visible dans
	 * cette fermeture — et enregistrer sans elle écrirait la valeur précédente.
	 */
	async function commit(next?: T) {
		const candidate = next === undefined ? value : next;
		if (candidate === saved.current) return;
		setState("saving");
		const ok = await save(candidate);
		saved.current = ok ? candidate : saved.current;
		setState(ok ? "saved" : "failed");
		// « Enregistré » s'efface : c'est un accusé de réception, pas un état.
		clearTimeout(timer.current ?? undefined);
		timer.current = setTimeout(() => setState("idle"), ok ? 2200 : 6000);
	}

	return { value, setValue, state, commit };
}
